/**
 * Step 6 — Hunger reflection — SYNTHESIS variant (v5)
 *
 * Spec: specs/onboarding-rebuild.md → Act 1 step 6
 *
 * Aha pattern (Mason's call, 2026-05-13):
 *   "Searching Scripture…" progress bar (~1.8s) → reveal cards.
 *
 * After step 5 picks, surface ONE curated Bible verse per pick as a
 * card carousel (or a single centered card if only one pick). The verse is
 * picked at random from a 5-verse pool per struggle; a small shuffle icon
 * on the card lets the user re-roll within the pool.
 *
 * If user picks nothing in step 5 (allowed per Mason's lock), surface a
 * 5-card "Where many Christians start" set — universal-starting verses,
 * different framing, no penalty for skipping.
 *
 * Card interactions (Mason's polish, 2026-05-13):
 *   - Card hover: scale-[1.02] + brightness-110 + gold-glow shadow (homepage
 *     pattern, scaled down for size). Border tightens from gold-dim to gold.
 *   - Shuffle icon (top-right): re-rolls to another verse from the pool.
 *   - Carousel arrows: pure scale on hover, NO movement (Mason: "I don't like
 *     them moving up").
 *
 * Continue button: shared `<ContinueButton />` so every step's CTA is
 * pixel-identical (Mason flagged inconsistency across steps).
 *
 * Data: `src/data/struggleVerses.js` (single source of truth). Pool counts
 * derive from `references.length` so the "Out of N verses" math is honest.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { STRUGGLE_VERSES, UNIVERSAL_STARTING, poolSizeFor } from '@/data/struggleVerses'
import ContinueButton from '@/components/onboarding/ContinueButton'

const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)'
const SEARCH_MS = 1800 // duration of the "Searching Scripture…" progress bar

// Legacy plain-English persona-fixture strings → snake_case keys.
const LEGACY_KEYS = {
  'staying consistent': 'staying_consistent',
  'sermon prep': 'sermon_prep',
  'Scripture comprehension': 'scripture_comprehension',
  'defending faith': 'defending_faith',
  'daily walk': 'daily_walk',
  'prayer life': 'prayer_life',
  doubt: 'doubt',
}

function normalizeKey(value) {
  if (!value) return null
  if (STRUGGLE_VERSES[value]) return value
  return LEGACY_KEYS[value] || null
}

// Module-scope chevron (lint static-components rule).
function ChevronIcon({ direction = 'right' }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      style={{ transform: direction === 'left' ? 'scaleX(-1)' : undefined }}
    >
      <path
        d="M6 4l4 4-4 4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

// Module-scope shuffle icon — refresh-style glyph in card corner.
function ShuffleIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M3 8a5 5 0 0 1 8.5-3.5L13 6V3"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M13 8a5 5 0 0 1-8.5 3.5L3 10v3"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

// Module-scope verse card. Handles its own hover state via inline handlers
// so we can do the homepage-style scale + glow without leaning on Tailwind
// hover utilities (which can fight the inline transitions).
function VerseCard({ eyebrow, text, citation, onShuffle, canShuffle, cardRef }) {
  const baseShadow = '0 1px 0 rgba(255,255,255,0.04) inset, 0 24px 48px -28px rgba(0,0,0,0.55)'
  const hoverShadow =
    '0 1px 0 rgba(255,255,255,0.06) inset, 0 24px 48px -24px rgba(0,0,0,0.6), 0 0 32px -4px rgba(212, 168, 67, 0.32)'

  return (
    <article
      ref={cardRef}
      className="flex-shrink-0 relative rounded-2xl p-6 sm:p-8 text-center"
      style={{
        scrollSnapAlign: 'center',
        width: 'min(420px, calc(100vw - 3rem))',
        background: 'var(--bg-surface)',
        border: '1px solid var(--gold-dim)',
        boxShadow: baseShadow,
        transition: `transform 220ms ${EASE}, border-color 220ms ${EASE}, box-shadow 240ms ${EASE}, filter 200ms ${EASE}`,
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'scale(1.02)'
        e.currentTarget.style.borderColor = 'var(--gold)'
        e.currentTarget.style.boxShadow = hoverShadow
        e.currentTarget.style.filter = 'brightness(1.05)'
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'scale(1)'
        e.currentTarget.style.borderColor = 'var(--gold-dim)'
        e.currentTarget.style.boxShadow = baseShadow
        e.currentTarget.style.filter = 'brightness(1)'
      }}
    >
      {/* Shuffle icon — top-right corner, gold-dim, hover brightens to gold.
          Only rendered if there's actually somewhere to shuffle to. */}
      {canShuffle ? (
        <button
          type="button"
          onClick={e => {
            e.stopPropagation()
            onShuffle?.()
          }}
          aria-label="Try another verse"
          className="absolute top-3 right-3 inline-flex items-center justify-center w-7 h-7 rounded-full hover:scale-110"
          style={{
            color: 'var(--gold-dim)',
            background: 'transparent',
            transition: `color 180ms ${EASE}, transform 180ms ${EASE}, background 180ms ${EASE}`,
          }}
          onMouseEnter={e => {
            e.currentTarget.style.color = 'var(--gold)'
            e.currentTarget.style.background = 'var(--gold-select)'
          }}
          onMouseLeave={e => {
            e.currentTarget.style.color = 'var(--gold-dim)'
            e.currentTarget.style.background = 'transparent'
          }}
        >
          <ShuffleIcon />
        </button>
      ) : null}

      <p
        className="text-[10px] uppercase tracking-[0.3em] font-semibold"
        style={{ color: 'var(--gold)', fontFamily: 'var(--font-body)' }}
      >
        {eyebrow}
      </p>
      <blockquote
        className="mt-6 italic text-[var(--text-primary)] text-[17px] sm:text-[19px] leading-[1.6]"
        style={{ fontFamily: 'var(--font-heading)' }}
      >
        <span aria-hidden="true" className="text-[var(--gold)] mr-1">
          &ldquo;
        </span>
        {text}
        <span aria-hidden="true" className="text-[var(--gold)] ml-1">
          &rdquo;
        </span>
      </blockquote>
      <p
        className="mt-6 text-[10px] uppercase tracking-[0.28em] font-semibold"
        style={{ color: 'var(--gold-dim)', fontFamily: 'var(--font-body)' }}
      >
        {citation}
      </p>
    </article>
  )
}

export default function HungerReflectionSynthesis({ struggles, topStruggle, onContinue }) {
  // --------- Build the cards from picks (or fall back to universal set) ---------
  const { mode, structuralCards, totalPool } = useMemo(() => {
    // Contract: if a caller passes the `struggles` prop at all (even as an
    // empty array), it's authoritative — they're using the new multi-pick
    // contract and "empty array" means "user picked nothing." Only fall
    // back to legacy `topStruggle` when `struggles` was never passed.
    const source = Array.isArray(struggles) ? struggles : topStruggle ? [topStruggle] : []
    const keys = source.map(normalizeKey).filter(Boolean)
    const uniqueKeys = [...new Set(keys)]

    if (uniqueKeys.length === 0) {
      // Empty / 0-picks: universal-starting set (different framing, real value).
      // Each universal card has only its one featured verse — no shuffle pool.
      return {
        mode: 'universal',
        structuralCards: UNIVERSAL_STARTING.cards.map(c => ({
          key: c.citation,
          eyebrow: c.eyebrow,
          verses: [{ text: c.text, citation: c.citation }],
        })),
        totalPool: 0,
      }
    }

    // Each struggle has a single `verses[]` array — all with text. The user
    // can cycle through every verse sequentially (Mason's 2026-05-13 lock).
    const built = uniqueKeys.map(k => ({
      key: k,
      eyebrow: STRUGGLE_VERSES[k].eyebrow,
      verses: STRUGGLE_VERSES[k].verses,
    }))
    return {
      mode: 'picks',
      structuralCards: built,
      totalPool: built.reduce((sum, c) => sum + c.verses.length, 0),
    }
  }, [struggles, topStruggle])

  // Per-card index into its featuredPool. Starts at 0 ("Verse 1 of N") and
  // increments sequentially on each shuffle, wrapping at the pool length.
  // Mason's call (2026-05-13): predictable sequential cycling beats random
  // shuffling so the position indicator reads honestly as the user clicks.
  const [cardVerseIndex, setCardVerseIndex] = useState(() => structuralCards.map(() => 0))

  const shuffleCard = useCallback(
    cardIdx => {
      setCardVerseIndex(prev => {
        const next = [...prev]
        const verseLen = structuralCards[cardIdx]?.verses.length || 1
        if (verseLen <= 1) return prev
        next[cardIdx] = (prev[cardIdx] + 1) % verseLen
        return next
      })
    },
    [structuralCards]
  )

  // Resolve the actual verse to show per card, given the current index.
  const cards = useMemo(
    () =>
      structuralCards.map((card, idx) => {
        const verse = card.verses[cardVerseIndex[idx] ?? 0] || card.verses[0]
        return {
          key: card.key,
          eyebrow: card.eyebrow,
          text: verse.text,
          citation: verse.citation,
          canShuffle: card.verses.length > 1,
        }
      }),
    [structuralCards, cardVerseIndex]
  )

  // --------- Searching progress bar ---------
  const [searchPct, setSearchPct] = useState(0)
  const [searchDone, setSearchDone] = useState(false)

  useEffect(() => {
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    if (reduced) {
      setSearchPct(100)
      setSearchDone(true)
      return undefined
    }
    let raf = 0
    let startTs = null
    const tick = ts => {
      if (startTs === null) startTs = ts
      const elapsed = ts - startTs
      const pct = Math.min(100, (elapsed / SEARCH_MS) * 100)
      setSearchPct(pct)
      if (pct < 100) {
        raf = requestAnimationFrame(tick)
      } else {
        setSearchDone(true)
      }
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [])

  // --------- Carousel navigation ---------
  const scrollRef = useRef(null)
  const cardRefs = useRef([])
  const [activeIndex, setActiveIndex] = useState(0)

  useEffect(() => {
    if (!searchDone) return undefined
    if (cards.length <= 1) return undefined
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
            const idx = cardRefs.current.indexOf(entry.target)
            if (idx >= 0) setActiveIndex(idx)
          }
        })
      },
      { root: scrollRef.current, threshold: [0, 0.6, 1] }
    )
    cardRefs.current.forEach(el => el && observer.observe(el))
    return () => observer.disconnect()
  }, [cards.length, searchDone])

  const goToCard = useCallback(idx => {
    const el = cardRefs.current[idx]
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
    }
  }, [])

  const goPrev = () => goToCard(Math.max(0, activeIndex - 1))
  const goNext = () => goToCard(Math.min(cards.length - 1, activeIndex + 1))

  // --------- Header text — single phrasing across all modes ---------
  // "Verses that might apply to you." with the word "Verses" in gold. Mason's
  // call (2026-05-13): drop the "out of N" header math; the indicator below
  // the active card carries the per-struggle pool count instead.
  // (Note: `mode`, `totalPool`, and `headerText` retained as locals in case
  // we want to surface them again; not currently rendered.)

  // Active-card pool position indicator. Denominator is verses.length —
  // the full curated verse list for the active struggle. User cycles through
  // every verse sequentially. Hidden when pool is 1 (universal-starting mode).
  const activeCard = structuralCards[activeIndex] ?? structuralCards[0]
  const activePos = (cardVerseIndex[activeIndex] ?? 0) + 1
  const activeTotal = activeCard?.verses?.length ?? 0
  const showPoolIndicator = activeTotal > 1

  // --------- Fade helper ---------
  const fadeIn = (delay = 0) => ({
    opacity: searchDone ? 1 : 0,
    transform: searchDone ? 'translateY(0)' : 'translateY(8px)',
    transition: `opacity 600ms ${EASE} ${delay}ms, transform 600ms ${EASE} ${delay}ms`,
  })

  const isSingle = cards.length === 1

  return (
    <div className="w-full max-w-xl md:max-w-2xl lg:max-w-3xl mx-auto px-6 py-10 sm:py-14">
      <style>{`
        .hr-syn-scroll::-webkit-scrollbar { display: none; }
        .hr-syn-scroll { -ms-overflow-style: none; scrollbar-width: none; scroll-snap-type: x mandatory; }
        /* Carousel arrows: pure scale on hover, NO translation (Mason's call). */
        .hr-syn-arrow { transition: opacity 200ms ease-out, background 200ms ease-out, transform 200ms ease-out, color 200ms ease-out; transform-origin: center; }
        .hr-syn-arrow:hover:not(:disabled) { background: var(--gold-select); color: var(--gold); transform: translateY(-50%) scale(1.15); }
        .hr-syn-arrow:disabled { opacity: 0.25; cursor: not-allowed; }
        .hr-syn-dot { transition: background 200ms ease-out, transform 200ms ease-out; }
        .hr-syn-dot:hover:not([aria-current="true"]) { background: var(--gold-dim) !important; transform: scale(1.2); }
      `}</style>

      {/* SEARCHING progress bar */}
      <div
        aria-live="polite"
        style={{
          opacity: searchDone ? 0 : 1,
          transform: searchDone ? 'translateY(-6px)' : 'translateY(0)',
          transition: 'opacity 420ms ease-out, transform 420ms ease-out',
          pointerEvents: 'none',
          height: searchDone ? 0 : 'auto',
          marginBottom: searchDone ? 0 : '1.5rem',
        }}
        className="max-w-[24rem] mx-auto text-center"
      >
        <p
          className="text-[10px] uppercase tracking-[0.32em] font-semibold"
          style={{ color: 'var(--gold)', fontFamily: 'var(--font-body)' }}
        >
          Searching Scripture
        </p>
        <div
          className="mt-3 h-[3px] rounded-full overflow-hidden mx-auto"
          style={{ background: 'var(--bg-surface-high)' }}
          role="progressbar"
          aria-valuenow={Math.round(searchPct)}
          aria-valuemin="0"
          aria-valuemax="100"
        >
          <div
            className="h-full rounded-full"
            style={{
              width: `${searchPct}%`,
              background: 'linear-gradient(90deg, var(--gold-dim) 0%, var(--gold) 100%)',
              transition: 'width 90ms linear',
            }}
          />
        </div>
        <p className="mt-2 text-[11px] text-[var(--text-muted-soft)]">for what you said&hellip;</p>
      </div>

      {/* RESULTS header — "Verses that might apply to you" with "Verses" gold */}
      <h1
        style={{ ...fadeIn(0), fontFamily: 'var(--font-heading)' }}
        className="text-[22px] sm:text-[26px] leading-[1.3] text-[var(--text-primary)] text-center max-w-[28ch] mx-auto"
      >
        <span style={{ color: 'var(--gold)' }}>Verses</span> that might apply to you.
      </h1>

      {/* CARDS */}
      {isSingle ? (
        <div className="mt-8 flex justify-center py-3" style={fadeIn(140)}>
          <VerseCard
            eyebrow={cards[0].eyebrow}
            text={cards[0].text}
            citation={cards[0].citation}
            canShuffle={cards[0].canShuffle}
            isActive={true}
            onShuffle={() => shuffleCard(0)}
            onCardClick={() => cards[0].canShuffle && shuffleCard(0)}
            cardRef={el => (cardRefs.current[0] = el)}
          />
        </div>
      ) : (
        <div className="relative mt-8" style={fadeIn(140)}>
          {/* Prev arrow — sm+ only */}
          <button
            type="button"
            onClick={goPrev}
            disabled={activeIndex === 0}
            aria-label="Previous verse"
            className="hr-syn-arrow hidden sm:flex absolute left-0 top-1/2 z-10 items-center justify-center w-9 h-9 rounded-full"
            style={{
              background: 'var(--bg-surface)',
              border: '1px solid var(--gold-dim)',
              color: 'var(--gold-dim)',
              transform: 'translateY(-50%)',
              marginLeft: '-12px',
            }}
          >
            <ChevronIcon direction="left" />
          </button>

          {/* Scroll container. Vertical padding (py-4) gives the card room to
              grow on hover (scale 1.02 + 32px gold-glow shadow). overflow-x:
              auto implicitly clips overflow-y per CSS spec — without padding,
              the scaled card + shadow get cut off at the top edge. */}
          <div
            ref={scrollRef}
            className="hr-syn-scroll flex gap-3 overflow-x-auto -mx-6 px-6 py-4 sm:-mx-4 sm:px-4"
          >
            {cards.map((card, idx) => (
              <VerseCard
                key={card.key}
                eyebrow={card.eyebrow}
                text={card.text}
                citation={card.citation}
                canShuffle={card.canShuffle}
                isActive={idx === activeIndex}
                onShuffle={() => shuffleCard(idx)}
                onCardClick={() => {
                  if (idx === activeIndex) {
                    if (card.canShuffle) shuffleCard(idx)
                  } else {
                    goToCard(idx)
                  }
                }}
                cardRef={el => (cardRefs.current[idx] = el)}
              />
            ))}
          </div>

          {/* Next arrow */}
          <button
            type="button"
            onClick={goNext}
            disabled={activeIndex === cards.length - 1}
            aria-label="Next verse"
            className="hr-syn-arrow hidden sm:flex absolute right-0 top-1/2 z-10 items-center justify-center w-9 h-9 rounded-full"
            style={{
              background: 'var(--bg-surface)',
              border: '1px solid var(--gold-dim)',
              color: 'var(--gold-dim)',
              transform: 'translateY(-50%)',
              marginRight: '-12px',
            }}
          >
            <ChevronIcon direction="right" />
          </button>
        </div>
      )}

      {/* Clickable dot indicator */}
      {cards.length > 1 ? (
        <div className="flex justify-center gap-2 mt-4" style={fadeIn(220)}>
          {cards.map((card, i) => (
            <button
              key={card.key}
              type="button"
              onClick={() => goToCard(i)}
              aria-label={`Go to verse ${i + 1}`}
              aria-current={i === activeIndex}
              className="hr-syn-dot block w-2 h-2 rounded-full"
              style={{
                background: i === activeIndex ? 'var(--gold)' : 'var(--bg-surface-high)',
                cursor: 'pointer',
              }}
            />
          ))}
        </div>
      ) : null}

      {/* Pool-position indicator — "Verse 3 of 22" subtle gray. Updates on
          shuffle (verse index in pool) AND on carousel navigation (total
          references for the now-active struggle). Hidden when there's
          nothing to count (universal-starting cards each have pool of 1). */}
      {showPoolIndicator ? (
        <p
          style={fadeIn(260)}
          className="text-center text-[10px] uppercase tracking-[0.28em] text-[var(--text-muted-soft)] mt-3"
          aria-live="polite"
          aria-atomic="true"
        >
          Verse {activePos} of {activeTotal}
        </p>
      ) : null}

      {/* Mobile swipe hint */}
      {cards.length > 1 ? (
        <p
          style={fadeIn(280)}
          className="sm:hidden mt-2 text-center text-[10px] uppercase tracking-[0.28em] text-[var(--text-muted-soft)]"
        >
          Swipe or tap dots
        </p>
      ) : null}

      {/* CTA — uses shared ContinueButton for funnel-wide consistency */}
      <div style={fadeIn(340)} className="mt-10 flex justify-center">
        <ContinueButton onClick={onContinue}>Continue</ContinueButton>
      </div>
    </div>
  )
}
